import React from 'react'
import { Box, VStack, Text, Spinner, Flex, Container } from '@chakra-ui/react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import BouncingLoader from './BouncingLoader'

export interface LoadingStateProps {
  message?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'spinner' | 'bouncing' | 'minimal'
  fullHeight?: boolean
  useContainer?: boolean
  bg?: string
  minHeight?: string
}

const LoadingState: React.FC<LoadingStateProps> = ({
  message,
  size = 'lg',
  variant = 'spinner',
  fullHeight = false,
  useContainer = false,
  bg = 'app.bg',
  minHeight
}) => {
  const { t } = useLanguage()

  const defaultMessage = message || t('common.loading')

  const getSpinnerSize = () => {
    switch (size) {
      case 'sm': return 'md'
      case 'md': return 'lg'
      case 'lg': return 'xl'
      case 'xl': return '2xl'
      default: return 'xl'
    }
  }

  const getSpinnerThickness = () => {
    switch (size) {
      case 'sm': return '2px'
      case 'md': return '3px'
      case 'lg': return '4px'
      case 'xl': return '5px'
      default: return '4px'
    }
  }

  const renderLoadingIndicator = () => {
    switch (variant) {
      case 'bouncing':
        return (
          <Flex gap={2} align="center" justify="center">
            <BouncingLoader width="12px" height="12px" delay={0} />
            <BouncingLoader width="12px" height="12px" delay={200} />
            <BouncingLoader width="12px" height="12px" delay={400} />
          </Flex>
        )
      case 'minimal':
        return null
      case 'spinner':
      default:
        return (
          <Spinner
            size={getSpinnerSize()}
            color="primaryScheme.500"
            thickness={getSpinnerThickness()}
          />
        )
    }
  }

  const content = (
    <Box
      bg={bg}
      minH={minHeight || (fullHeight ? '100vh' : '50vh')}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <VStack spacing={4}>
        {renderLoadingIndicator()}
        <Text
          color="app.text.secondary"
          fontSize={size === 'sm' ? 'sm' : size === 'xl' ? 'lg' : 'md'}
          textAlign="center"
        >
          {defaultMessage}
        </Text>
      </VStack>
    </Box>
  )

  if (useContainer) {
    return (
      <Container maxW="container.xl" py={8}>
        {content}
      </Container>
    )
  }

  return content
}

export default LoadingState
